import { useState } from 'react';
import { Input, useToast } from '@caios/ui';

/**
 * Approving a proposed fix (a WordPress security update, a service restart) right where it lands — the Bridge and the
 * Pocket's Needs you — instead of opening Monitoring. Only owners and admins may decide; the server says so to anyone else.
 * The fix's id rides in the Inbox item's link (/admin?tab=monitoring&fix=<id>).
 */
export const fixIdOf = (link: string) => /[?&]fix=([0-9a-f-]{36})/.exec(link)?.[1] ?? null;

async function post(path: string, body: unknown = {}) {
  const r = await fetch(path, { method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
  const b = await r.json().catch(() => ({}));
  return { ok: r.ok, status: r.status, error: (b as { error?: string }).error ?? `Request failed (${r.status})` };
}
/** Decide one fix, then clear its Inbox item. A fix someone already decided (409) clears too: there is nothing left to do. */
async function decide(fixId: string, itemId: string, approve: boolean, reason?: string) {
  const r = await post(`/api/v1/monitor/fixes/${fixId}/${approve ? 'approve' : 'decline'}`, approve ? {} : { reason });
  if (!r.ok && r.status !== 409) throw new Error(r.error);
  await post(`/api/v1/inbox/${itemId}/done`);
  return r.ok ? (approve ? 'approved' : 'declined') : 'already decided';
}

export function FixButtons({ link, itemId, onDecided }: { link: string; itemId: string; onDecided: () => void }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [declining, setDeclining] = useState<string | null>(null);
  const id = fixIdOf(link);
  if (!id) return null;
  const run = async (approve: boolean) => {
    setBusy(true);
    try { const r = await decide(id, itemId, approve, declining ?? undefined); toast(<>{r === 'approved' ? 'Approved — it runs within a minute and is checked after' : r === 'declined' ? 'Declined' : 'Already decided'}</>); onDecided(); }
    catch (e) { toast(<>{(e as Error).message}</>, 'fire'); } finally { setBusy(false); setDeclining(null); }
  };
  if (declining !== null) return (
    <form style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }} onSubmit={(e) => { e.preventDefault(); if (declining.trim()) void run(false); }}>
      <Input autoFocus value={declining} onChange={(e) => setDeclining(e.target.value)} placeholder="Why not?" aria-label="Why not" data-testid="fix-decline-reason" style={{ minHeight: 32, fontSize: 13 }} />
      <button type="submit" className="cx-btn cx-btn--sm" disabled={busy || !declining.trim()}>Decline</button>
      <button type="button" className="cx-btn cx-btn--sm" onClick={() => setDeclining(null)}>Cancel</button>
    </form>
  );
  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
      <button type="button" className="cx-btn cx-btn--sm cx-btn--primary" disabled={busy} onClick={() => void run(true)} data-testid="fix-approve-inline">Approve</button>
      <button type="button" className="cx-btn cx-btn--sm" disabled={busy} onClick={() => setDeclining('')} data-testid="fix-decline-inline">Decline</button>
    </span>
  );
}

/** One tap for every fix waiting in the Inbox: approve them all, one after another. */
export function ApproveAllFixes({ items, onDone }: { items: { id: string; kind: string; link: string }[]; onDone: () => void }) {
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const fixes = items.filter((i) => i.kind === 'approval' && fixIdOf(i.link));
  if (fixes.length < 2) return null;
  const run = async () => {
    if (!window.confirm(`Approve all ${fixes.length} fixes? Each WordPress update runs on its live site and is checked after it runs.`)) return;
    let ok = 0, failed = 0;
    for (const [n, i] of fixes.entries()) {
      setBusy(`${n + 1} of ${fixes.length}`);
      try { await decide(fixIdOf(i.link)!, i.id, true); ok += 1; } catch { failed += 1; }
    }
    setBusy(null);
    toast(<>Approved {ok} fix{ok === 1 ? '' : 'es'}{failed ? ` · ${failed} could not be approved` : ''} — they run one at a time and are checked after</>, failed ? 'fire' : undefined);
    onDone();
  };
  return <button type="button" className="cx-btn cx-btn--sm cx-btn--primary" disabled={!!busy} onClick={() => void run()} data-testid="fix-approve-all">{busy ? `Approving ${busy}…` : `Approve all ${fixes.length} fixes`}</button>;
}
